import React from "react";
import Select from "../../../elements/Select";
import UserRolesCard from "../users/UserRolesCard";
import EmployeeSoftSkillsCard from "./EmployeeSoftSkillsCard";
import EmployeeTrainingsCard from "./EmployeeTrainingsCard";
import EmployeeCommendationsCard from "./EmployeeCommendationsCard";
import EmployeeInfractionsCard from "./EmployeeInfractionsCard";
import EmployeeCertificationsCard from "./EmployeeCertificationsCard";
import EmployeeMedExamsCard from "./EmployeeMedExamsCard";
import EmployeeAbsencesCard from "./EmployeeAbsencesCard";
import EmployeePositionsCard from "./EmployeePositionsCard";
import TextInput from "../../../elements/TextInput";
import EmployeeAvailabilitiesCard from "./EmployeeAvailabilitiesCard";
import EmployeeScheduleRulesCard from "./EmployeeScheduleRulesCard";
import EmployeeEvaluationCard from "./EmployeeEvaluationCard";
import EmployeeAssetsCard from "./EmployeeAssetsCard";
import EmployeeTrainingManagementCard from "./EmployeeTrainingManagementCard";
import EmployeeBusinessTripsCard from "./EmployeeBusinessTripsCard";

const CompanySection = ({title, description, children, id, wide = false}) => (
  <section className={`employee-form-section employee-company-section ${wide ? "employee-section-wide" : ""}`.trim()} id={id}>
    <div className="employee-form-section-header">
      <strong>{title}</strong>
      {description && <span>{description}</span>}
    </div>
    <div className="employee-company-section-content">{children}</div>
  </section>
);

const CompanyField = ({label, required = false, children, span = 1}) => (
  <div className={`employee-form-field employee-span-${span}`}>
    <label>{required && <span className="required">*</span>} {label}</label>
    <div className="employee-form-control">{children}</div>
  </div>
);

const TabEmployeeCompanyData = ({
  state,
  rolesList,
  softSkillsList,
  trainingsList,
  commendationsList,
  infractionsList,
  certificationTypesList,
  medExamTypesList,
  supervisorsList,
  absenceTypesList,
  departmentsList,
  positionsList,
  settings,
  canManageRoles = false
}) => {
  const manager = state.currentItem?.department?.manager;
  const isAdding = !!state.currentFormState?.formAddingDataMode;
  const selectedHiringStage = (state.hiringStagesList || []).find(
    stage => Number(stage?.id || 0) === Number(state.currentItem?.hiringStage?.id || 0)
  ) || state.currentItem?.hiringStage;
  const departmentRequired = !!selectedHiringStage?.departmentRequired;
  const positionRequired = departmentRequired && !!selectedHiringStage?.positionRequired;

  return (
    <div className="employee-company-layout employee-section-columns">
      <CompanySection
        id="employment-organization"
        title="Zatrudnienie i organizacja"
        description="Firma zatrudniająca, etap zatrudnienia i podstawowa jednostka organizacyjna."
      >
        <div className="employee-form-grid employee-company-top-grid">
          <CompanyField label="Firma zatrudniająca">
            <Select
              label="Firma zatrudniająca"
              propertyName="hiringCompany"
              required={false}
              state={state}
              itemsList={state.hiringCompaniesList}
              itemName="hiringCompany"
              displayNames={["shortName"]}
            />
          </CompanyField>

          <CompanyField label="Etap zatrudnienia">
            <Select
              label="Etap zatrudnienia"
              propertyName="hiringStage"
              required={false}
              state={state}
              itemsList={state.hiringStagesList}
              itemName="hiringStage"
              displayNames={["name"]}
            />
          </CompanyField>

          <CompanyField label="Dział główny" required={departmentRequired}>
            <Select
              label="Dział główny"
              propertyName="department"
              required={departmentRequired}
              state={state}
              itemsList={departmentsList}
              itemName="department"
              displayNames={["name"]}
            />
            <small>
              {departmentRequired
                ? positionRequired
                  ? "Wybrany etap zatrudnienia wymaga działu i co najmniej jednego stanowiska z tego działu."
                  : "Wybrany etap zatrudnienia wymaga działu, ale stanowisko jest opcjonalne."
                : "Dział i stanowisko są opcjonalne dla wybranego etapu zatrudnienia."}
            </small>
          </CompanyField>

          <CompanyField label="Menedżer">
            <div className={`employee-manager-badge ${manager ? "assigned" : "missing"}`}>
              {manager ? `${manager.firstName} ${manager.lastName}` : "Brak przypisanego menedżera"}
            </div>
          </CompanyField>
        </div>
      </CompanySection>

      <CompanySection
        id="positions"
        title={positionRequired ? "Stanowiska *" : "Stanowiska"}
        description={isAdding
          ? "Przy dodawaniu pracownika lista stanowisk jest ograniczana do wybranego działu."
          : "Stanowiska, stawki, oceny oraz wymagania szkoleniowe i certyfikacyjne."}
        wide
      >
        {positionRequired && (
          <div className="employee-inline-info mb-2">
            Wybrany etap zatrudnienia wymaga przypisania co najmniej jednego aktywnego stanowiska z wybranego działu.
          </div>
        )}
        <div className="employee-section-block">
          <EmployeePositionsCard state={state} positionsList={positionsList} settings={settings}/>
        </div>
      </CompanySection>

      {!isAdding && (
        <>
          <CompanySection
            id="company-roles"
            title="Role i uprawnienia"
            description="Uprawnienia pracownika do funkcji aplikacji."
          >
            {canManageRoles ? (
              state.currentItem?.appUser ? (
                <UserRolesCard state={state} rolesList={rolesList || []}/>
              ) : (
                <div className="employee-inline-info">
                  Ten pracownik nie ma konta użytkownika Qstaff. Role można przypisywać po utworzeniu konta w zakładce Dane osobowe.
                </div>
              )
            ) : (
              <div className="employee-inline-info">Role konta Qstaff są zarządzane wyłącznie przez administratora.</div>
            )}
          </CompanySection>

          <CompanySection
            id="soft-skills"
            title="Kompetencje"
            description="Umiejętności i kompetencje miękkie pracownika."
          >
            <div className="employee-section-block">
              <EmployeeSoftSkillsCard state={state} skillsList={softSkillsList} settings={settings}/>
            </div>
            <div className="employee-form-grid employee-description-grid">
              <CompanyField label="Opis umiejętności" span={4}>
                <TextInput propertyName="employeeSkills" required={true} state={state}/>
              </CompanyField>
            </div>
          </CompanySection>

          <CompanySection
            id="evaluations"
            title="Ocena ogólna"
            description="Automatyczne podsumowanie oceny pracownika."
          >
            <EmployeeEvaluationCard employeeId={state.currentItem?.id} supervisorsList={supervisorsList} settings={settings}/>
          </CompanySection>

          <CompanySection
            id="trainings"
            title="Szkolenia"
            description="Historia szkoleń oraz zarządzanie szkoleniami pracownika."
            wide
          >
            <div className="employee-section-block">
              <EmployeeTrainingsCard state={state} trainingsList={trainingsList} supervisorsList={supervisorsList} settings={settings}/>
            </div>
            <EmployeeTrainingManagementCard employeeId={state.currentItem?.id}/>
          </CompanySection>

          <CompanySection
            id="certifications"
            title="Certyfikaty"
            description="Certyfikaty, terminy ważności i wymagania stanowiskowe."
            wide
          >
            <div className="employee-section-block">
              <EmployeeCertificationsCard state={state} certificationTypesList={certificationTypesList} settings={settings}/>
            </div>
          </CompanySection>

          <CompanySection
            id="employee-events"
            title="Pochwały i przewinienia"
            description="Zdarzenia wpływające na ocenę i historię pracownika."
            wide
          >
            <div className="employee-section-block" id="commendations">
              <EmployeeCommendationsCard state={state} commendationsList={commendationsList} supervisorsList={supervisorsList} settings={settings}/>
            </div>
            <div className="employee-section-block" id="infractions">
              <EmployeeInfractionsCard state={state} infractionsList={infractionsList} supervisorsList={supervisorsList} settings={settings}/>
            </div>
          </CompanySection>

          <CompanySection
            id="health-absence"
            title="Zdrowie i nieobecności"
            description="Badania lekarskie oraz ewidencja nieobecności pracownika."
            wide
          >
            <div className="employee-section-block" id="medexams">
              <EmployeeMedExamsCard state={state} medExamTypesList={medExamTypesList} settings={settings}/>
            </div>
            <div className="employee-section-block" id="absences">
              <EmployeeAbsencesCard state={state} absenceTypesList={absenceTypesList} supervisorsList={supervisorsList} settings={settings}/>
            </div>
          </CompanySection>

          <CompanySection
            id="working-time"
            title="Czas pracy i dostępność"
            description="Reguły planowania i indywidualna dostępność pracownika."
            wide
          >
            <div className="employee-section-block" id="schedule-rules">
              <EmployeeScheduleRulesCard state={state}/>
            </div>
            <div className="employee-section-block" id="availabilities">
              <EmployeeAvailabilitiesCard state={state} settings={settings}/>
            </div>
          </CompanySection>

          <CompanySection
            id="employee-settlements"
            title="Rozliczenia pracownika"
            description="Kaucje pobrane od pracownika oraz wydane vouchery."
            wide
          >
            <EmployeeAssetsCard employeeId={state.currentItem?.id}/>
          </CompanySection>

          <CompanySection
            id="business-trips"
            title="Wyjazdy służbowe"
            description="Delegacje i wyjazdy powiązane bezpośrednio z kartoteką pracownika."
            wide
          >
            <EmployeeBusinessTripsCard employeeId={state.currentItem?.id}/>
          </CompanySection>
        </>
      )}
    </div>
  );
};

export default TabEmployeeCompanyData;
